/**
 * IMPORTANT: English comments only. DO NOT use Chinese characters in comments.
 * All Chinese comments will be removed during code reviews.
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { LocateFixed, Map, ChevronRight, Plus, MapPin, Trash2, Edit, X, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAddressContext } from '../../context/AddressContext';
import { useLocationManager } from '../../hooks/useLocationManager';
import { AddressWithId } from '../../services/addressService';
import { SearchBar } from './SearchBar';
import { useMapStore, ensurePlacesApiLoaded } from '../../lib/map/store';
import { GOOGLE_MAPS_API_KEY, mapLibraries } from '../../lib/map/constants';
import { ensureGoogleMapsLoaded } from '../../lib/map/utils';

// Declare global window type extension for Google Maps callback
declare global {
  interface Window {
    initGoogleMapsCallback?: () => void;
  }
}

interface AddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAddress: (address: AddressWithId) => void;
  selectedAddress?: AddressWithId | null;
  isSubscription?: boolean;
  onOpenMapModal?: () => void;
  onEditAddress?: (address: AddressWithId) => void;
  
  // Optional props to receive data from parent component
  addresses?: AddressWithId[];
  isLoading?: boolean;
  error?: string | null;
  handleDeleteAddress?: (address: AddressWithId, index: number) => Promise<boolean>;
}

export default function AddressModal({
  isOpen,
  onClose,
  onSelectAddress,
  selectedAddress,
  isSubscription = false,
  onOpenMapModal,
  onEditAddress,
  // Accept data from parent if provided
  addresses: externalAddresses,
  isLoading: externalLoading,
  error: externalError,
  handleDeleteAddress: externalHandleDeleteAddress
}: AddressModalProps) {
  console.log('[AddressModal] Rendering with isOpen:', isOpen);
  console.log('[AddressModal] onSelectAddress type:', typeof onSelectAddress);
  
  const { t } = useTranslation();
  
  // Add stability tracking
  const [isStable, setIsStable] = useState(false);
  
  // Track Maps API loading state
  const [mapsApiLoaded, setMapsApiLoaded] = useState(false);
  const apiLoadingRef = useRef(false);
  const scriptLoadAttemptedRef = useRef(false);
  
  // Add state for selected location from search
  const [selectedLocation, setSelectedLocation] = useState<{
    position: { lat: number, lng: number } | null,
    address: string,
    locationName: string
  }>({
    position: null,
    address: '',
    locationName: ''
  });
  
  // Use the AddressContext instead of the useAddressManager hook
  const {
    addresses: contextAddresses,
    isLoading: contextLoading,
    error: contextError,
    refreshAddresses,
    deleteAddress
  } = useAddressContext();
  
  // Get location management functions from the hook (which now uses store)
  const { handleCurrentLocationSelect } = useLocationManager();
  
  // Get current location data directly from the store
  const currentLocation = useMapStore(state => state.currentLocation);
  
  // Helper function to directly load Google Maps API script
  const loadGoogleMapsScript = useCallback(() => {
    // Skip if already loading or already loaded
    if (apiLoadingRef.current || scriptLoadAttemptedRef.current || 
        (window.google && window.google.maps)) {
      return;
    }
    
    console.log('[AddressModal] Directly adding Google Maps script to DOM');
    scriptLoadAttemptedRef.current = true;
    apiLoadingRef.current = true;
    
    // Create the script element
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=${mapLibraries.join(',')}&v=weekly&callback=initGoogleMapsCallback`;
    script.async = true;
    script.defer = true;
    
    // Add global callback function
    window.initGoogleMapsCallback = () => {
      console.log('[AddressModal] Google Maps API loaded via script tag');
      setMapsApiLoaded(true);
      apiLoadingRef.current = false;
    };
    
    // Handle errors
    script.onerror = () => {
      console.error('[AddressModal] Error loading Google Maps script directly');
      apiLoadingRef.current = false;
    };
    
    // Add the script to the document
    document.head.appendChild(script);
    
    // Return cleanup function
    return () => {
      if (window.initGoogleMapsCallback) {
        delete window.initGoogleMapsCallback;
      }
    };
  }, []);
  
  // Preload Google Maps API when modal opens
  useEffect(() => {
    if (isOpen) {
      // Check if Maps API is already loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        console.log('[AddressModal] Google Maps API already loaded, skipping preload');
        setMapsApiLoaded(true);
        return;
      }
      
      // If not loaded and not currently loading, try to load directly
      if (!mapsApiLoaded && !apiLoadingRef.current) {
        console.log('[AddressModal] Modal opened, preloading Google Maps API...');
        
        // Direct script loading approach
        loadGoogleMapsScript();
        
        // Also try the utility function as backup
        const backupTimer = setTimeout(() => {
          if (!window.google || !window.google.maps) {
            // Try backup loading method
            console.log('[AddressModal] Attempting backup loading method...');
            apiLoadingRef.current = true;
            
            const loadGoogleMapsApi = async () => {
              try {
                // First ensure base Maps API is loaded
                const mapsLoaded = await ensureGoogleMapsLoaded(20000);
                if (!mapsLoaded) {
                  console.error('[AddressModal] Backup method failed to load Google Maps API');
                  apiLoadingRef.current = false;
                  return;
                }
                
                // Then ensure Places API is loaded
                const placesLoaded = await ensurePlacesApiLoaded();
                if (!placesLoaded) {
                  console.error('[AddressModal] Backup method failed to load Places API');
                  apiLoadingRef.current = false;
                  return;
                }
                
                console.log('[AddressModal] Successfully loaded Google Maps and Places API with backup method');
                setMapsApiLoaded(true);
                apiLoadingRef.current = false;
              } catch (error) {
                console.error('[AddressModal] Error in backup Google Maps API loading:', error);
                apiLoadingRef.current = false;
              }
            };
            
            loadGoogleMapsApi();
          }
        }, 5000); // Give direct loading 5 seconds to work before trying backup
        
        return () => clearTimeout(backupTimer);
      }
    }
  }, [isOpen, mapsApiLoaded, loadGoogleMapsScript]);
  
  // Custom function to open map modal with selected location
  const openMapWithLocation = () => {
    console.log('[AddressModal] openMapWithLocation called, selectedLocation:', selectedLocation);
    
    // If we have location data from search, pass it to the parent component
    if (selectedLocation.position && onOpenMapModal) {
      // Get the current store state
      const storeState = useMapStore.getState();
      console.log('[AddressModal] Current store state before map open:', {
        markerPosition: storeState.markerPosition,
        addressDetails: storeState.addressDetails,
        address: storeState.address,
        locationName: storeState.locationName
      });
      
      // Store selected location in map store before opening the modal
      const { setMarkerPosition, updatePosition, setAddress, setLocationName } = storeState;
      
      // Make sure we have the latitude and longitude as numbers
      const position = {
        lat: typeof selectedLocation.position.lat === 'number' ? 
          selectedLocation.position.lat : 
          parseFloat(String(selectedLocation.position.lat)),
        lng: typeof selectedLocation.position.lng === 'number' ? 
          selectedLocation.position.lng : 
          parseFloat(String(selectedLocation.position.lng))
      };
      
      console.log('[AddressModal] Setting position in store before opening map:', position);
      
      // Set programmatic update flag to prevent race conditions
      useMapStore.setState({ isProgrammaticUpdate: true });
      
      // Also update addressDetails in store to ensure map gets all info
      useMapStore.setState({
        addressDetails: {
          latitude: position.lat,
          longitude: position.lng,
          formatted_address: selectedLocation.address,
          name: selectedLocation.locationName
        }
      });
      
      // Update position after setting addressDetails
      setMarkerPosition(position);
      updatePosition(position);
      setAddress(selectedLocation.address || '');
      setLocationName(selectedLocation.locationName || '');
      
      console.log('[AddressModal] Store updated, now opening map modal');
      
      // Now open the map modal which will use these values
      setTimeout(() => {
        if (onOpenMapModal) {
          onOpenMapModal();
          console.log('[AddressModal] Map modal opened');
          
          // Clear programmatic update flag after a short delay
          setTimeout(() => {
            useMapStore.setState({ isProgrammaticUpdate: false });
          }, 300);
        }
      }, 50);
    } else if (onOpenMapModal) {
      console.log('[AddressModal] No location selected, opening map normally');
      // Just open the map modal normally if no location selected
      onOpenMapModal();
    } else {
      console.warn('[AddressModal] Cannot open map modal - onOpenMapModal is undefined!');
    }
  };
  
  // When the modal is opened, mark it as stable after a short delay
  useEffect(() => {
    if (isOpen) {
      // Reset stability when opening
      setIsStable(false);
      
      // Set stable after a short delay
      const stabilityTimer = setTimeout(() => {
        console.log('[AddressModal] Modal is now stable');
        setIsStable(true);
      }, 300);
      
      return () => clearTimeout(stabilityTimer);
    }
  }, [isOpen]);
  
  // Safe onClose wrapper with logging
  const safeOnClose = () => {
    console.log('[AddressModal] Close requested explicitly by user action');
    onClose();
  };
  
  // Determine if we should use external data, context data, or internal data
  // Priority: 1. External data (passed as props), 2. Context data, 3. Internal data (legacy)
  const useExternalData = !!externalAddresses;
  
  // Determine which data sources to use
  const addresses = useExternalData ? externalAddresses! : contextAddresses;
  const isLoading = useExternalData ? externalLoading! : contextLoading;
  const error = useExternalData ? externalError! : contextError;
  
  // Wrapper for delete address function
  const handleDeleteAddress = async (address: AddressWithId, index: number): Promise<boolean> => {
    if (useExternalData && externalHandleDeleteAddress) {
      return externalHandleDeleteAddress(address, index);
    } else {
      // Use context delete function
      return deleteAddress(address.id);
    }
  };
  
  // Stop showing loading state if we have an empty array of addresses (means loading is complete, just no addresses)
  const [hasConfirmedEmptyAddresses, setHasConfirmedEmptyAddresses] = useState(false);
  
  useEffect(() => {
    if (isLoading && addresses && addresses.length === 0) {
      console.log('[AddressModal] Have empty addresses array but still loading, stopping loading state');
      setHasConfirmedEmptyAddresses(true);
    }
  }, [isLoading, addresses]);
  
  // Create derived loading state - hide loading if we've confirmed empty addresses
  const actualLoadingState = isLoading && !hasConfirmedEmptyAddresses;
  
  // Validate selectedAddress is in the current addresses list
  useEffect(() => {
    if (selectedAddress && addresses.length > 0) {
      const addressExists = addresses.some(addr => addr.id === selectedAddress.id);
      console.log('[AddressModal] Selected address comparison:', {
        selectedAddressId: selectedAddress.id,
        firstAddressId: addresses[0].id,
        match: addressExists,
        addressIdsInList: addresses.map(a => a.id).join(', ')
      });
      
      if (!addressExists) {
        console.warn('[AddressModal] Selected address not found in current user addresses', {
          selectedAddressId: selectedAddress.id
        });
        // Just display the address list without auto-selecting
        // Removed auto-selection of first address to prevent immediate API calls
      }
    }
  }, [addresses, selectedAddress, onSelectAddress]);
  
  // Create a wrapper that ensures map opens even if location detection fails
  const handleCurrentLocationWithFallback = useCallback(async () => {
    try {
      console.log('[AddressModal] Getting current location before opening map...');
      
      // Wait for current location to be obtained
      const coords = await handleCurrentLocationSelect();
      
      if (coords) {
        console.log('[AddressModal] Current location obtained:', coords);
        
        // Update the map store with current location data
        const { setMarkerPosition, updatePosition, setAddress, setLocationName } = useMapStore.getState();
        
        // Set the position in the store
        setMarkerPosition(coords);
        updatePosition(coords);
        
        // Wait a bit for the store to update and then get the latest current location data
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Get the updated current location from store
        const updatedCurrentLocation = useMapStore.getState().currentLocation;
        
        // Set address and location name from current location data
        if (updatedCurrentLocation.address) {
          setAddress(updatedCurrentLocation.address);
          setLocationName('Current Location');
        } else {
          // Fallback if address is not available yet
          setLocationName('Current Location');
          setAddress('Getting address...');
        }
        
        console.log('[AddressModal] Store updated with current location, opening map...');
      } else {
        console.log('[AddressModal] Failed to get current location, opening map with default location');
      }
    } catch (error) {
      console.log('[AddressModal] Current location detection failed:', error);
    }
    
    // Open map modal (with or without current location)
    openMapWithLocation();
  }, [handleCurrentLocationSelect, openMapWithLocation]);
  
  // Debug log addresses whenever they change
  useEffect(() => {
    console.log(`[AddressModal] Addresses state updated, count: ${addresses.length}`);
    if (addresses.length > 0) {
      console.log('[AddressModal] First address:', addresses[0].id);
    }
  }, [addresses]);
  
  // Update when addresses prop changes
  useEffect(() => {
    if (useExternalData && externalAddresses) {
      console.log(`[AddressModal] External addresses updated, count: ${externalAddresses.length}`);
      // No need to update isLoading directly as it's derived from externalLoading
    }
  }, [useExternalData, externalAddresses]);
  
  // Initialize by loading addresses when component mounts or when modal opens
  // ONLY if we're not using external data
  const fetchedRef = useRef({
    hasEverFetched: false,
    fetchCount: 0
  });
  
  useEffect(() => {
    console.log('[AddressModal] Modal open state changed to:', isOpen);
    
    // Only fetch if using internal data management
    if (isOpen && !useExternalData && !fetchedRef.current.hasEverFetched) {
      console.log('[AddressModal] Modal opened, fetching addresses from context...');
      fetchedRef.current.fetchCount++;
      fetchedRef.current.hasEverFetched = true;
      
      // Use a small delay to ensure the API call doesn't conflict with other initialization
      const fetchTimer = setTimeout(() => {
        refreshAddresses().then(addresses => {
          console.log(`[AddressModal] Fetched ${addresses.length} addresses from context`);
          
          // Force a component update if addresses were returned but not reflected in state
          if (addresses.length > 0) {
            console.log('[AddressModal] Addresses found in API response');
          }
        }).catch(err => {
          console.error('[AddressModal] Error fetching addresses:', err);
        });
      }, 100);
      
      return () => clearTimeout(fetchTimer);
    } else if (useExternalData) {
      console.log('[AddressModal] Using external addresses data, skipping fetch');
    }
  // Only run this effect on mount and when isOpen changes, not on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, useExternalData]);
  
  // Search input reference
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Safe onSelectAddress wrapper with debouncing
  const handleSelectAddress = useCallback((address: AddressWithId) => {
    console.log('[AddressModal] handleSelectAddress called with address:', address.id);
    
    // Don't reselect the same address
    if (selectedAddress && address.id === selectedAddress.id) {
      console.log('[AddressModal] Skipping selection of already selected address:', address.id);
      return;
    }
    
    // Create a stable clone to avoid reference issues
    const addressCopy = {...address};
    onSelectAddress(addressCopy);
    
    // Close the modal immediately after selection
    console.log('[AddressModal] Closing modal after address selection');
    safeOnClose();
  }, [onSelectAddress, selectedAddress, safeOnClose]);
  
  // Render the address list items with memoization
  const renderAddressList = useMemo(() => {
    return addresses.map((address, index) => {
      const isSelected = selectedAddress && selectedAddress.id === address.id;
      
      return (
        <div
          key={address.id}
          className={`flex items-center justify-between py-3 px-4 mb-3 ${
            isSelected ? 'bg-[#FFF9E6] border-[#F9D348] border-2' : 'bg-white border'
          } rounded-lg cursor-pointer`}
          onClick={() => handleSelectAddress(address)}
        >
          {/* Selection indicator */}
          {isSelected && (
            <div className="mr-3">
              <div className="w-6 h-6 bg-[#F9D348] rounded-full flex items-center justify-center">
                <Check size={14} className="text-black" />
              </div>
            </div>
          )}
          
          {/* Address content */}
          <div className="flex-1">
            <h3 className="font-medium text-gray-800 text-sm">
              {address.addressLabel && (
                <span className="bg-[#FFF9E6] text-black px-2 py-0.5 rounded mr-2 font-semibold border border-[#F9D348]">
                  {address.addressLabel}
                </span>
              )}
              {address.name || address.locationName || address.street || t('components.addressModal.unnamed')}
            </h3>
            <p className="text-sm text-gray-500 mt-1">{address.street}</p>
            <p className="text-sm text-gray-500">
              {address.city}
              {address.state ? `, ${address.state}` : ''}
              {address.zipCode ? ` ${address.zipCode}` : ''}
            </p>
          </div>
          
          {/* Actions for editing and removing */}
          <div className="flex space-x-3 ml-3">
            {onEditAddress && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditAddress(address);
                }}
                className="p-2 text-gray-600 hover:text-gray-800"
              >
                <Edit size={16} />
              </button>
            )}
            
            {handleDeleteAddress && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteAddress(address, index);
                }}
                className="p-2 text-gray-600 hover:text-gray-800"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        </div>
      );
    });
  }, [addresses, selectedAddress, handleSelectAddress, handleDeleteAddress, onEditAddress, t]);
  
  // Early return after all hooks are declared
  if (!isOpen) {
    return null;
  }

  console.log('[AddressModal] Rendering modal content for addresses:', addresses.length);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-none sm:rounded-xl w-full h-full sm:max-w-md sm:h-auto sm:max-h-[90vh] overflow-y-auto">
        {/* Header with increased padding */}
        <div className="bg-white p-3 sm:p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 z-10">
          <h1 className="text-lg sm:text-xl font-bold">{t('map.addressModal.title')}</h1>
          <button
            onClick={safeOnClose}
            className="text-gray-500 hover:text-gray-700 p-1.5"
          >
            <X size={18} className="sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Search Bar - Increased padding and explicit height */}
        <div className="p-4 sm:p-5 border-b min-h-[60px] flex items-center">
          <div className="w-full" style={{ minHeight: '44px' }}>
            <SearchBar 
              inputRef={searchInputRef} 
              className="bg-white py-3 text-sm"
              onSearch={(query) => {
                // Handle place selection from SearchBar
                const { markerPosition, address, locationName } = useMapStore.getState();
                
                // Save the selected location data
                setSelectedLocation({
                  position: markerPosition,
                  address,
                  locationName: locationName || query
                });
                
                console.log('[AddressModal] Search selection:', {
                  position: markerPosition,
                  address,
                  locationName
                });
                
                // Open map modal with the selected location
                openMapWithLocation();
              }}
              onShowMapModal={onOpenMapModal}
            />
          </div>
        </div>

        {/* Current Location - Increased padding and spacing */}
        <div 
          className={`py-3 px-4 sm:p-4 border-b flex items-center justify-between ${
            currentLocation.loading ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'
          }`}
          onClick={currentLocation.loading ? undefined : handleCurrentLocationWithFallback}
        >
          <div className="flex items-center">
            <div className="bg-gray-100 w-9 h-9 rounded-full flex items-center justify-center mr-3">
              <LocateFixed size={18} className={currentLocation.loading ? "text-blue-500 animate-pulse" : "text-gray-500"} />
            </div>
            <div>
              <h2 className="text-sm font-bold">{t('map.addressModal.currentLocation')}</h2>
              <p className="text-gray-500 text-xs mt-1">
                {currentLocation.loading 
                  ? t('map.gettingLocation') 
                  : t('map.addressModal.useCurrentLocation')
                }
              </p>
            </div>
          </div>
          <button 
            className="focus:outline-none border-none bg-transparent"
          >
            <ChevronRight size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Choose on Map - Increased padding and spacing */}
        <div 
          className="py-3 px-4 sm:p-4 border-b flex items-center justify-between cursor-pointer"
          onClick={openMapWithLocation}
        >
          <div className="flex items-center">
            <div className="bg-gray-100 w-9 h-9 rounded-full flex items-center justify-center mr-3">
              <Map size={18} className="text-gray-500" />
            </div>
            <div>
              <h2 className="text-sm font-bold">{t('map.addressModal.chooseOnMap')}</h2>
            </div>
          </div>
          <button className="focus:outline-none border-none bg-transparent">
            <ChevronRight size={18} className="text-gray-500" />
          </button>
        </div>
        
        {/* Main Content - Increased padding and spacing */}
        <div className="p-3 sm:p-4 pb-14 sm:pb-16">
          {/* Add New Address Button - More spacious */}
          <button
            onClick={openMapWithLocation}
            className="w-full bg-white border border-dashed border-gray-300 rounded-lg p-3 flex items-center justify-center text-gray-600 hover:border-gray-400 transition-colors mb-4"
          >
            <Plus size={18} className="mr-2" />
            <span className="text-sm font-medium">{t('map.addressModal.addNewAddress')}</span>
          </button>

          {/* Loading State */}
          {actualLoadingState && (
            <div className="text-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">{t('map.addressModal.loading')}</p>
            </div>
          )}

          {/* Error State - More spacious */}
          {error && !actualLoadingState && (
            <div className="bg-red-50 text-red-600 p-3 text-sm rounded-lg mb-4">
              {error}
              {!useExternalData && (
                <button 
                  onClick={(e) => refreshAddresses()} 
                  className="ml-2 underline"
                >
                  {t('map.addressModal.retry')}
                </button>
              )}
            </div>
          )}

          {/* Empty State - More spacious */}
          {!actualLoadingState && !error && addresses.length === 0 && (
            <div className="text-center py-6">
              <div className="mb-3">
                <MapPin size={36} className="text-gray-400 mx-auto" />
              </div>
              <p className="text-gray-600 text-sm font-medium mb-2">{t('map.addressModal.noAddresses')}</p>
              <p className="text-gray-500 text-xs mb-3">{t('map.addressModal.addToStart')}</p>
              <button 
                onClick={openMapWithLocation}
                className="mt-2 bg-[#F9D348] text-black font-semibold py-2.5 px-5 text-sm rounded-full"
              >
                {t('map.addressModal.addNewAddress')}
              </button>
            </div>
          )}

          {/* Address List */}
          {!actualLoadingState && addresses.length > 0 && (
            <div className="space-y-4">
              {renderAddressList}
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 